import {
  Button,
  Card,
  Modal,
  Pagination,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  notification,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { formatedDate } from 'utils/common';
import PageTitle from '../../components/page-title';
import styles from './styles.module.scss';
import CustomButton from 'components/custom-button';
import { DeleteOutlined, PlusOutlined, SendOutlined } from '@ant-design/icons';
import { shortenName } from 'utils/short-name';
import moment from 'moment';
import ModalCreateJobNotification from './component/ModalCreateJobNotification';
import { TYPE_JOB } from '../../constants/common';
import { JOB_TYPE_COLOR, JOB_TYPE_LABEL, isJobSent, jobStatus } from './labels';

const LIMIT = 10;

const initFilter = { page: 1, limit: LIMIT };

export interface LiveShowFilter {
  limit: number;
  page: number;
  startDate?: string;
  endDate?: string;
}

const JobNotificationManager = () => {
  const [loading, setLoading] = useState(true);
  const [dataResponse, setDataResponse] = useState<any>({
    items: [],
    totalCount: 0,
  });
  const [filter, setFilter] = useState(initFilter as LiveShowFilter);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [statusUpdateData, setStatusUpdateData] = useState(false);
  const [isOpenModalEditAndCreate, setIsOpenModalEditAndCreate] = useState(false);
  const [valueUserSelected, setvalueUserSelected] = useState<any>(null);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  const hasSelected = selectedRowKeys.length > 0;

  useEffect(() => {
    const fetchPks = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.jobNotification, {
          ...filter,
        });
        setDataResponse({ items: res?.data?.data, totalCount: res?.data?.metadata?.total });
      } finally {
        setLoading(false);
      }
    };

    fetchPks();
  }, [filter, statusUpdateData]);

  // Bảng 7 cột trong khung 956px (màn 1280): Tiêu đề + Nội dung gộp một cột "Tin nhắn"
  // co giãn, các cột còn lại rộng cố định. Bảng dàn cố định (tableLayout="fixed") nên chữ
  // dài — kể cả link không có dấu cách — xuống dòng/cắt "…" trong ô chứ không đẩy cột khác.
  const columns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 72,
      key: 'id',
    },
    {
      title: 'Tin nhắn',
      dataIndex: 'content',
      key: 'content',
      render: (value, record) => (
        <div className={styles.message}>
          {record.title ? (
            <Tooltip title={shortenName(record.title, 300)}>
              <span className={styles.messageTitle}>{record.title}</span>
            </Tooltip>
          ) : null}
          <Tooltip title={shortenName(value || '', 300)}>
            <span className={styles.messageContent}>{value || ''}</span>
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Cách gửi',
      dataIndex: 'type',
      key: 'type',
      width: 128,
      render: (value) =>
        value ? (
          <Tag color={JOB_TYPE_COLOR[value] || 'default'} className={styles.tag}>
            {JOB_TYPE_LABEL[value] || value}
          </Tag>
        ) : (
          ''
        ),
    },

    {
      title: 'Lịch gửi',
      dataIndex: 'calendarDate',
      key: 'calendarDate',
      width: 160,
      render: (value) => (
        <span className={styles.nowrap}>
          {value ? `${formatedDate(value)} ${moment(value).format('HH:mm')}` : '—'}
        </span>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      // "Quá lịch, chưa gửi" là nhãn dài nhất (~120px + 2 × 16px đệm ô).
      width: 160,
      render: (_, record) => {
        const status = jobStatus(record);
        return (
          <Tag color={status.color} className={styles.tag}>
            {status.label}
          </Tag>
        );
      },
    },

    {
      title: 'Thao tác',
      width: 112,
      key: 'action',
      render: (_, record) =>
        record.type === TYPE_JOB.NO ? (
          <Popconfirm
            title="Gửi tin này vào nhóm ngay?"
            okText="Gửi"
            cancelText="Hủy"
            onConfirm={() => handleRunJobNotification(record)}
          >
            <Button size="small" icon={<SendOutlined />}>
              {isJobSent(record) ? 'Gửi lại' : 'Gửi'}
            </Button>
          </Popconfirm>
        ) : (
          <Tooltip title="Chỉ tin “Gửi thủ công” mới bấm gửi được">
            <span className={styles.muted}>—</span>
          </Tooltip>
        ),
    },
  ];
  const onChangeStatusUpdateData = () => setStatusUpdateData(!statusUpdateData);

  const handleChangePage = (page: number) => {
    setFilter((preState: any) => ({ ...preState, page }));
  };

  const handelOpenDeleteModal = () => {
    setIsModalDeleteOpen(true);
  };

  const handleOkDeleteModal = async () => {
    setLoading(true);
    try {
      await AxiosUtils._delete(API.deleteJob, { ids: selectedRowKeys });
      onChangeStatusUpdateData();
      notification.success({
        message: 'Thành công',
        description: 'Xóa thành công!',
        duration: 4,
      });
      setSelectedRowKeys([]);
    } catch (error) {
      notification.error({
        message: 'Không thành công',
        description: 'Có lỗi xảy ra, vui lòng thử lại.',
        duration: 4,
      });
    } finally {
      setLoading(false);
      setIsModalDeleteOpen(false);
    }
  };

  const handleCancelDeleteModal = () => {
    setIsModalDeleteOpen(false);
  };

  const handelOpenCreateEditModal = (value: any) => {
    setIsOpenModalEditAndCreate(true);
    setvalueUserSelected(value);
  };

  const handleRunJobNotification = async (job: any) => {
    setLoading(true);
    try {
      await AxiosUtils.post(`${API.jobNotification}/${job.id}`);
      onChangeStatusUpdateData();
      notification.success({
        message: 'Thành công',
        description: 'Gửi thành công!',
        duration: 4,
      });
    } catch (e) {
      notification.error({
        message: 'Không thành công',
        description: 'Có lỗi xảy ra, vui lòng thử lại.',
        duration: 4,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCreateEditModal = () => {
    setIsOpenModalEditAndCreate(false);
    setvalueUserSelected(null);
  };

  return (
    <div>
      <PageTitle
        title="Tin nhắn thông báo"
        extra={
          <Space wrap>
            {hasSelected && (
              <Button danger icon={<DeleteOutlined />} onClick={handelOpenDeleteModal}>
                Xóa {selectedRowKeys.length} tin
              </Button>
            )}
            <CustomButton
              onClick={() => handelOpenCreateEditModal(valueUserSelected)}
              bgColor="primary-green"
              text="Thêm tin nhắn tự động"
              icon={<PlusOutlined />}
            />
          </Space>
        }
      />
      <Card
        className="custom-table"
        styles={{ body: { padding: 0 } }}
        style={{ overflow: 'hidden' }}
      >
        <Table
          rowSelection={rowSelection}
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={dataResponse.items}
          tableLayout="fixed"
          // Bảng trống + scroll.x: antd đặt ô "Trống" rộng bằng cả khung (tính cả viền 1px
          // hai bên) nên bảng rộng hơn khung 2px và hiện thanh cuộn ngang thừa. Chỉ cuộn khi có dòng.
          // 880 = các cột cố định (~680) + tối thiểu ~200px cho cột "Tin nhắn": khung hẹp hơn
          // thì cuộn ngang thay vì bóp cột chữ.
          scroll={dataResponse.items?.length ? { x: 880 } : undefined}
          loading={loading}
        />
        {dataResponse.totalCount > LIMIT && (
          <div className={styles.pagination}>
            <Pagination
              showSizeChanger={false}
              current={filter.page}
              onChange={handleChangePage}
              showLessItems
              total={dataResponse.totalCount}
              pageSize={LIMIT}
            />
          </div>
        )}
      </Card>

      {isOpenModalEditAndCreate && (
        <ModalCreateJobNotification
          isOpen={isOpenModalEditAndCreate}
          handleCancelCreateEditModal={handleCancelCreateEditModal}
          valueUserSelected={valueUserSelected}
          onChangeStatusUpdateData={onChangeStatusUpdateData}
        />
      )}
      <Modal
        title="Xóa tin nhắn tự động"
        open={isModalDeleteOpen}
        onOk={handleOkDeleteModal}
        onCancel={handleCancelDeleteModal}
        okText="Xóa"
        cancelText="Hủy"
        okButtonProps={{ danger: true }}
        confirmLoading={loading}
      >
        <p>Bạn có chắc muốn xóa {selectedRowKeys.length} tin đã chọn?</p>
      </Modal>
    </div>
  );
};

export default JobNotificationManager;
